import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Star, Save } from "lucide-react";
import { api_get, api_post, api_put, api_delete, apiError } from "../../lib/api";

const blankReview = {
    authorName: "",
    authorPhoto: "",
    rating: 5,
    text: "",
    relativeTime: "Recently",
    source: "google",
    isActive: true,
    displayOrder: 100,
};

const ManageReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [settings, setSettings] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blankReview);

    const load = async () => {
        try {
            const [rev, s] = await Promise.all([
                api_get("/admin/reviews/manual"),
                api_get("/admin/reviews/settings"),
            ]);
            setReviews(rev);
            setSettings(s || {});
        } catch (err) {
            toast.error(apiError(err));
        }
    };
    useEffect(() => {
        load();
    }, []);

    const open = (it = null) => {
        setEditing(it ? it.id : "new");
        setForm(it ? { ...blankReview, ...it } : blankReview);
    };
    const close = () => setEditing(null);

    const saveReview = async (e) => {
        e.preventDefault();
        if (!form.authorName || !form.text) return toast.error("Name and text required.");
        try {
            const payload = { ...form, rating: Number(form.rating), displayOrder: Number(form.displayOrder) || 100 };
            if (editing === "new") await api_post("/admin/reviews/manual", payload);
            else await api_put(`/admin/reviews/manual/${editing}`, payload);
            toast.success("Saved.");
            close();
            await load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete review?")) return;
        try {
            await api_delete(`/admin/reviews/manual/${id}`);
            toast.success("Deleted.");
            await load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        try {
            await api_put("/admin/reviews/settings", settings);
            toast.success("Settings saved.");
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    if (!settings) return <div className="p-6">Loading…</div>;

    return (
        <div data-testid="manage-reviews">
            <h1 className="font-display text-2xl text-ink font-bold mb-1">Reviews</h1>
            <p className="text-ink-light text-sm mb-6">Google Reviews settings · Manual reviews used as fallback when Google API is unavailable.</p>

            {/* === GOOGLE REVIEWS SETTINGS === */}
            <form onSubmit={saveSettings} className="bg-white rounded-2xl border border-cream-200 p-6 mb-8">
                <h2 className="font-display text-lg text-ink font-bold mb-4">Google Reviews settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <S label="Section title" v={settings.sectionTitle} onChange={(v) => setSettings({ ...settings, sectionTitle: v })} />
                    <S label="Section subtitle" v={settings.sectionSubtitle} onChange={(v) => setSettings({ ...settings, sectionSubtitle: v })} />
                    <S label="Badge text" v={settings.badgeText} onChange={(v) => setSettings({ ...settings, badgeText: v })} />
                    <S label="Rating text" v={settings.ratingText} onChange={(v) => setSettings({ ...settings, ratingText: v })} />
                    <S label="Secondary text" v={settings.secondaryText} onChange={(v) => setSettings({ ...settings, secondaryText: v })} />
                    <S label="Button text" v={settings.buttonText} onChange={(v) => setSettings({ ...settings, buttonText: v })} />
                    <S label="Button URL" v={settings.buttonUrl} onChange={(v) => setSettings({ ...settings, buttonUrl: v })} />
                    <S label="Google review link" v={settings.googleReviewLink} onChange={(v) => setSettings({ ...settings, googleReviewLink: v })} />
                    <S label="Google Place ID" v={settings.googlePlaceId} onChange={(v) => setSettings({ ...settings, googlePlaceId: v })} testId="rs-place-id" />
                    <S label="Autoplay (ms)" type="number" v={settings.autoplayMs} onChange={(v) => setSettings({ ...settings, autoplayMs: Number(v) })} />
                    <S label="Max reviews" type="number" v={settings.maxReviews} onChange={(v) => setSettings({ ...settings, maxReviews: Number(v) })} />
                    <S label="Rating override (e.g. 4.8 — leave 0 to auto-compute)" type="number" v={settings.ratingOverride || 0} onChange={(v) => setSettings({ ...settings, ratingOverride: Number(v) || null })} testId="rs-rating-override" />
                    <S label="Total reviews override (e.g. 6 — leave 0 to auto-count)" type="number" v={settings.totalReviewsOverride || 0} onChange={(v) => setSettings({ ...settings, totalReviewsOverride: Number(v) || null })} testId="rs-total-override" />
                    <label className="flex items-center gap-2 text-sm mt-7" data-testid="rs-google-toggle">
                        <input
                            type="checkbox"
                            checked={!!settings.googleReviewsEnabled}
                            onChange={(e) => setSettings({ ...settings, googleReviewsEnabled: e.target.checked })}
                        />
                        Enable Google Places API (requires GOOGLE_PLACES_API_KEY in backend env + Place ID above)
                    </label>
                </div>
                <div className="flex justify-end mt-5">
                    <button type="submit" className="px-5 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold flex items-center gap-1.5" data-testid="save-review-settings">
                        <Save className="w-4 h-4" /> Save settings
                    </button>
                </div>
            </form>

            {/* === MANUAL REVIEWS === */}
            <header className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg text-ink font-bold">Manual reviews (fallback)</h2>
                <button onClick={() => open()} className="px-4 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold text-sm flex items-center gap-1.5" data-testid="add-review">
                    <Plus className="w-4 h-4" /> New review
                </button>
            </header>
            <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-cream-100 text-ink-light">
                        <tr>
                            <th className="text-left px-4 py-3">Author</th>
                            <th className="text-left px-4 py-3">Rating</th>
                            <th className="text-left px-4 py-3">Text</th>
                            <th className="text-left px-4 py-3">Order</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {reviews.map((r) => (
                            <tr key={r.id} className="border-t border-cream-200">
                                <td className="px-4 py-3">
                                    <div className="font-semibold">{r.authorName}</div>
                                    <div className="text-xs text-ink-light">{r.relativeTime} · {r.source}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex">
                                        {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />)}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-ink-light text-xs max-w-md">
                                    <div className="line-clamp-2">{r.text}</div>
                                </td>
                                <td className="px-4 py-3">{r.displayOrder}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => open(r)} className="p-1.5 rounded hover:bg-cream-100"><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
                    <form onSubmit={saveReview} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6 shadow-xl">
                        <header className="flex items-center justify-between mb-5">
                            <h2 className="font-display text-xl font-bold">{editing === "new" ? "New review" : "Edit review"}</h2>
                            <button type="button" onClick={close}><X className="w-5 h-5" /></button>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <S label="Author name" v={form.authorName} onChange={(v) => setForm({ ...form, authorName: v })} testId="rev-name" />
                            <S label="Author photo URL" v={form.authorPhoto} onChange={(v) => setForm({ ...form, authorPhoto: v })} />
                            <S label="Rating (1-5)" type="number" v={form.rating} onChange={(v) => setForm({ ...form, rating: Number(v) })} />
                            <S label="Relative time" v={form.relativeTime} onChange={(v) => setForm({ ...form, relativeTime: v })} />
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Source</label>
                                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white text-sm">
                                    <option value="google">Google</option>
                                    <option value="website">Website</option>
                                    <option value="whatsapp">WhatsApp</option>
                                </select>
                            </div>
                            <S label="Display order" type="number" v={form.displayOrder} onChange={(v) => setForm({ ...form, displayOrder: Number(v) })} />
                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Text</label>
                                <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white text-sm" data-testid="rev-text" />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-cream-200">Cancel</button>
                            <button type="submit" className="px-5 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold" data-testid="rev-save">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const S = ({ label, v, onChange, type = "text", testId }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}</label>
        <input
            type={type}
            value={v ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white text-sm"
            data-testid={testId}
        />
    </div>
);

export default ManageReviews;
